import { type ChangeEvent, type FC, type ReactNode } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { DataGridPro, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid-pro";
import { useIntl } from "react-intl";

import { type ClosingCarton } from "@/schemas";

interface ClosingCartonsTableProps {
	readonly cartons: ClosingCarton[];
	readonly columns: GridColDef<ClosingCarton>[];
	readonly totalCount: number;
	readonly page: number;
	readonly rowsPerPage: number;
	readonly onPageChange: (event: unknown, newPage: number) => void;
	readonly onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement>) => void;
	readonly emptyState?: ReactNode;
}

const DefaultNoRowsOverlay: FC = () => {
	const intl = useIntl();
	return <Typography>{intl.formatMessage({ id: "noRowsToDisplay", defaultMessage: "No rows to display" })}</Typography>;
};

export const ClosingCartonsTable: FC<ClosingCartonsTableProps> = ({
	cartons,
	columns,
	totalCount,
	page,
	rowsPerPage,
	onPageChange,
	onRowsPerPageChange,
	emptyState,
}) => {
	const theme = useTheme();
	const paginationModel: GridPaginationModel = {
		page,
		pageSize: rowsPerPage,
	};

	const handlePaginationModelChange = (newModel: GridPaginationModel) => {
		if (newModel.pageSize !== rowsPerPage) {
			onRowsPerPageChange({ target: { value: String(newModel.pageSize) } } as ChangeEvent<HTMLInputElement>);
		} else if (newModel.page !== page) {
			onPageChange(null, newModel.page);
		}
	};

	const NoRowsOverlay = emptyState ? () => emptyState : DefaultNoRowsOverlay;

	return (
		<Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
			<DataGridPro
				rows={cartons}
				columns={columns}
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={handlePaginationModelChange}
				rowCount={totalCount}
				pageSizeOptions={[5, 10, 25, 50]}
				disableRowSelectionOnClick
				disableColumnSelector
				disableColumnResize
				getRowId={(row) => row.closingId}
				slots={{
					noRowsOverlay: NoRowsOverlay,
				}}
				sx={{
					border: "none",
					"& .MuiDataGrid-columnHeader:focus": {
						outline: "none",
					},
					"& .MuiDataGrid-columnHeader:focus-within": {
						outline: "none",
					},
					"& .MuiDataGrid-columnSeparator": {
						display: "none",
					},
					"& .MuiDataGrid-overlayWrapperInner": {
						minHeight: 240,
						padding: 4,
					},
					"& .MuiTablePagination-root": {
						overflow: "hidden",
					},
					"& .MuiDataGrid-row:nth-of-type(odd)": {
						backgroundColor: theme.palette.grey[100],
					},
				}}
			/>
		</Box>
	);
};
